import * as SX from './sx';
import * as Styled from './styles';

import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { classnames, fNumber, toPercent } from '@aibees/shared/utils';

import { Icon } from '../icon';
import { Label } from '../label';
import type { ProgressBarProps as Props } from './types';
import { memo } from 'react';

export const ProgressBar = memo(
  ({
    className,
    icon,
    iconColor = 'primary.500',
    title,
    total,
    remaining,
    used,
    color,
    format = fNumber,
    showPercentage,
    ...rest
  }: Props) => {
    // toPercent guards the zero denominator (returns 0), avoiding 0/0 = NaN.
    const percentage =
      total !== undefined && remaining !== undefined
        ? Math.round(Math.min(toPercent(remaining, total), 100))
        : undefined;

    const showHeader =
      icon !== undefined || title !== undefined || total !== undefined;
    const showFooter = remaining !== undefined || used !== undefined;

    return (
      <Styled.Wrapper
        className={classnames('ProgressBar', className)}
        sx={SX.root}
      >
        {showHeader && (
          <Styled.Top>
            <Styled.Column>
              {icon && <Icon name={icon} size={16} color={iconColor} />}
              {title !== undefined && (
                <Typography variant="body3" color="grey.700">
                  {title}
                </Typography>
              )}
            </Styled.Column>
            <Stack direction="row" alignItems="center" gap={0.5}>
              {total !== undefined && (
                <Typography variant="body4" color="grey.500">
                  <Typography
                    component="span"
                    variant="body4"
                    fontWeight={700}
                    color="grey.700"
                  >
                    {format(total)}
                  </Typography>{' '}
                  total
                </Typography>
              )}
              {showPercentage && percentage !== undefined && (
                <Label size="xxxsmall" color={color} sx={{ width: 'auto' }}>
                  {percentage}%
                </Label>
              )}
            </Stack>
          </Styled.Top>
        )}
        <Box>
          <LinearProgress
            color={color}
            variant="determinate"
            value={percentage ?? 0}
            {...rest}
          />
        </Box>
        {showFooter && (
          <Styled.Bottom>
            {remaining !== undefined && (
              <Typography variant="body4" color="grey.500">
                <Typography
                  component="span"
                  variant="body4"
                  fontWeight={700}
                  color="grey.700"
                >
                  {format(remaining)}
                </Typography>{' '}
                remaining
              </Typography>
            )}
            {used !== undefined && (
              <Typography variant="body4" color="grey.500">
                <Typography
                  component="span"
                  variant="body4"
                  fontWeight={700}
                  color="grey.700"
                >
                  {format(used)}
                </Typography>{' '}
                used
              </Typography>
            )}
          </Styled.Bottom>
        )}
      </Styled.Wrapper>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';
